import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchJobsStatus } from '@/api';
import { LoadingPage } from '@/components/common/Loading';
import { Badge } from '@/components/ui/badge';
import { Briefcase, Clock, CheckCircle, XCircle, Brain } from 'lucide-react';
import { formatDateTime, formatRelative } from '@/lib/formatters';

export default function JobsStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const isAI = location.pathname === '/admin/jobs/ai';

  useEffect(() => {
    fetchJobsStatus().then(res => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <LoadingPage />;

  return (
    <div data-testid="jobs-status-page">
      <h1 className="text-2xl font-bold tracking-tight uppercase text-slate-50 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
        <Briefcase className="w-5 h-5 inline mr-2 text-blue-400" />
        Jobs
      </h1>

      {/* Tabs: System Jobs / AI Content Jobs */}
      <div className="flex items-center gap-1 mb-6 border-b border-slate-800 pb-2" data-testid="jobs-tabs">
        <Link to="/admin/jobs"
          className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
            !isAI ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-500 hover:text-slate-300 border border-transparent'
          }`}>
          <Briefcase className="w-3.5 h-3.5 inline mr-1.5" />System Jobs
        </Link>
        <Link to="/admin/jobs/ai"
          className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
            isAI ? 'bg-purple-500/15 text-purple-300 border border-purple-500/30' : 'text-slate-500 hover:text-slate-300 border border-transparent'
          }`}>
          <Brain className="w-3.5 h-3.5 inline mr-1.5" />AI Content Jobs
        </Link>
      </div>

      {/* Scheduler Info */}
      {data?.scheduler && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 mb-6">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            Auto Scheduler
          </h3>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${data.scheduler.api_key_configured ? 'bg-emerald-400' : 'bg-red-400'}`} />
              <span className="text-slate-400">API Key: {data.scheduler.api_key_configured ? 'Configured' : 'Missing'}</span>
            </div>
            <span className="text-slate-400">Sync Interval: <span className="text-emerald-400 font-bold">{data.scheduler.sync_interval_minutes} min</span></span>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {data?.jobs?.length > 0 ? data.jobs.map((job, i) => (
          <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-lg p-4" data-testid={`job-row-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {job.status === 'success'
                  ? <CheckCircle className="w-4 h-4 text-emerald-400" />
                  : <XCircle className="w-4 h-4 text-red-400" />}
                <span className="text-sm font-medium text-slate-200">{job.provider_name}</span>
                <Badge className={job.status === 'success' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}>
                  {job.status}
                </Badge>
              </div>
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatRelative(job.started_at)}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span>Matches: <span className="text-slate-300">{job.matches_fetched}</span></span>
              <span>Odds: <span className="text-slate-300">{job.odds_fetched}</span></span>
              <span>Errors: <span className={job.errors_count > 0 ? 'text-red-400' : 'text-slate-300'}>{job.errors_count}</span></span>
              <span>Started: {formatDateTime(job.started_at)}</span>
            </div>
          </div>
        )) : (
          <div className="text-center py-12 text-slate-500">No job records</div>
        )}
      </div>
    </div>
  );
}
